import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  buildRegistry,
  businessDayFetchRange,
  checkInTime,
  computeActivityCoverage,
  computeActivityPresence,
  computeDayWorkedHours,
  evaluateMetric,
  formatMetric,
  profileOutputsForDay,
  staffingConfigFromRows,
  summaryMetrics,
  todayIsoParis,
  workloadFromDay,
  assignEmployeeColors,
  metricColor,
  type ActivityOverride,
  type ActivityPresence,
  type ActivityQualification,
  type BoardActivity,
  type BoardDay,
  type DayPresence,
  type Employee,
  type MetricDef,
  type StaffingConfigSnapshot,
} from '@lemontaigu/staff';
import { useBoard, useMetricRegistry, useStaffingConfig, useTimesheet } from '@lemontaigu/staff/react';
import { FrDateInput } from '../ui/FrDateInput.js';
import { PresenceMatrix } from './PresenceMatrix.js';
import { EmployeeQuickEdit } from './EmployeeQuickEdit.js';
import {
  fmtMonthLabel,
  isPlannedDay,
  monthDays,
  monthStart,
  monthWeeks,
  shiftDay,
  shiftMonthStart,
} from './logic.js';

// The « Mois » view (S4 projections): a read-only calendar overview — per
// day the cleaning load, dinner covers, and a gap badge; click a day to
// open it in the Jour view. Unplanned days (no load, no declarations)
// render neutral — same isPlannedDay rule as the week Gantt. Below the
// calendar, a compact « Présence » matrix (2026-07-19, PresenceMatrix): one
// row per day, one column per employee grouped by team, each cell coding the
// declared/assigned thirds (matin/après-midi/soir) or a full-red Absent.
// showHours (2026-07-19, bos@ only via the Planning prop): additionally
// fetches staff_time_entries for the month and overlays each cell with the
// actual worked hours (computeDayWorkedHours) — reception@'s desk never sets
// this, so its fetch/render path is untouched.

// Row shapes and their mappers moved into @lemontaigu/staff at ladder step 6 —
// these were duplicated verbatim in DayView and EmployeeWeek. What remains here
// is the view's own concern: grouping already-mapped domain objects by day.
const groupByDay = <T extends { date: string }>(rows: T[]): Map<string, T[]> => {
  const map = new Map<string, T[]>();
  for (const r of rows) map.set(r.date, [...(map.get(r.date) ?? []), r]);
  return map;
};
const absenceIdsByDay = (rows: { date: string; employeeId: string }[]): Map<string, Set<string>> => {
  const map = new Map<string, Set<string>>();
  for (const r of rows) {
    const set = map.get(r.date) ?? new Set<string>();
    set.add(r.employeeId);
    map.set(r.date, set);
  }
  return map;
};

const WEEKDAY_HEADERS = ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.'];

export function MonthView({
  activities,
  qualifications,
  employees,
  onOpenDay,
  showHours = false,
  canAssign = false,
}: {
  activities: BoardActivity[];
  qualifications: ActivityQualification[];
  employees: Employee[];
  onOpenDay: (day: string) => void;
  /**
   * Présence matrix: clicking a cell opens the employee-first quick-edit
   * (repos / assignations) for that employee and day (2026-08-04). Same gate as
   * the ★ assignment control — `rh.board.assign` is what the policy behind
   * these writes tests, so a session without it would only get a refusal.
   */
  canAssign?: boolean;
  /** Présence matrix: also fetch + show each day's actual worked hours per employee (2026-07-19, bos@ only). */
  showHours?: boolean;
}) {
  const board = useBoard();
  const timesheet = useTimesheet();
  const staffingConfig = useStaffingConfig();
  const metricRegistry = useMetricRegistry();
  const [month, setMonth] = useState(() => monthStart(todayIsoParis()));
  const weeks = useMemo(() => monthWeeks(month), [month]);

  const [boardDays, setBoardDays] = useState<Map<string, BoardDay>>(new Map());
  const [overrides, setOverrides] = useState<Map<string, ActivityOverride[]>>(new Map());
  const [presencesByDay, setPresencesByDay] = useState<Map<string, ActivityPresence[]>>(new Map());
  const [absencesByDay, setAbsencesByDay] = useState<Map<string, Set<string>>>(new Map());
  const [hoursByDay, setHoursByDay] = useState<Map<string, Map<string, number>> | undefined>(undefined);
  const [staffingSnapshot, setStaffingSnapshot] = useState<StaffingConfigSnapshot | null>(null);
  const [metrics, setMetrics] = useState<MetricDef[]>([]);
  const [loaded, setLoaded] = useState(false);

  // Configuration, loaded once and never allowed to fail the month: without it
  // the cell simply shows no summary figures rather than the grid breaking.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const snap = await staffingConfig.load();
        if (!cancelled) setStaffingSnapshot(snap);
      } catch {
        if (!cancelled) setStaffingSnapshot(null);
      }
      try {
        const defs = await metricRegistry.load();
        if (!cancelled) setMetrics(defs);
      } catch {
        if (!cancelled) setMetrics([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [staffingConfig, metricRegistry]);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState<{ employee: Employee; day: string } | null>(null);

  const load = useCallback(
    async (start: string) => {
      setErr('');
      setLoaded(false);
      const end = shiftMonthStart(start, 1);
      // clock_in is timestamptz, not date — bound it with real ISO instants,
      // not the bare 'YYYY-MM-DD' strings the date-typed board_* columns above
      // compare against directly. businessDayFetchRange returns a UTC-anchored
      // superset covering the 04:00 business-day overhang; computeDayWorkedHours
      // buckets exactly and the matrix only reads the visible days' keys.
      const { fromIso: startIso, toIso: endIso } = businessDayFetchRange(start, end);
      // The port's range is INCLUSIVE (PostgREST gte/lte); `end` here is the
      // first day of the next month, so step back one day to keep the exact
      // window this view has always fetched.
      const endInclusive = shiftDay(end, -1);
      try {
        const [days, overrideRows, presence, absences, t] = await Promise.all([
          board.days(start, endInclusive),
          board.overrides(start, endInclusive),
          board.presence(start, endInclusive),
          board.absences(start, endInclusive),
          // Worked hours are C3's TIMESHEET aggregate, not the board's — a
          // separate port, deliberately, rather than more methods on BoardStore.
          showHours ? timesheet.entriesBetween(startIso, endIso) : Promise.resolve([]),
        ]);
        setBoardDays(new Map(days.map((d) => [d.date, d])));
        setOverrides(groupByDay(overrideRows));
        setPresencesByDay(groupByDay(presence));
        setAbsencesByDay(absenceIdsByDay(absences));
        setHoursByDay(showHours ? computeDayWorkedHours(t) : undefined);
        setLoaded(true);
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    },
    [showHours, board, timesheet],
  );

  useEffect(() => {
    void load(month);
  }, [month, load]);

  const currentIds = useMemo(() => employees.map((e) => e.id), [employees]);
  const chipColors = useMemo(() => assignEmployeeColors(employees.map((e) => e.id)), [employees]);
  const today = todayIsoParis();
  const thisMonth = monthStart(today);

  const gapCount = (key: string): number =>
    computeActivityCoverage({
      date: key,
      activities,
      overrides: overrides.get(key) ?? [],
      day: boardDays.get(key) ?? null,
      presences: presencesByDay.get(key) ?? [],
      currentEmployeeIds: currentIds,
      absentEmployeeIds: absencesByDay.get(key) ?? [],
    }).gaps.length;

  // « Présence » matrix (2026-07-19): days × employees-grouped-by-team.
  const days = useMemo(() => monthDays(month), [month]);
  const presenceByDay = useMemo(() => {
    const map = new Map<string, Map<string, DayPresence>>();
    for (const key of days) {
      map.set(
        key,
        computeActivityPresence({
          date: key,
          activities,
          overrides: overrides.get(key) ?? [],
          presences: presencesByDay.get(key) ?? [],
          absentEmployeeIds: absencesByDay.get(key) ?? [],
          currentEmployeeIds: currentIds,
        }),
      );
    }
    return map;
  }, [days, activities, overrides, presencesByDay, absencesByDay, currentIds]);

  // ── The day's headline figures (ADR-0015 « afficher en résumé ») ─────────
  // Was hard-coded « {departures} ch. · {covers} cvts », read straight off the
  // row: the month disagreed with the week and the day about what a headline
  // IS, and none of the three could be changed without a deploy. The cell now
  // renders whichever metrics carry the flag, in the operator's own order.
  //
  // Unlike Semaine/Jour this shows BASE metrics too — there is no entry grid
  // here, so « Départs » is not already on screen.
  const registry = useMemo(() => buildRegistry(metrics), [metrics]);
  const summaryDefs = useMemo(() => summaryMetrics(registry), [registry]);
  const specs = useMemo(
    () =>
      staffingSnapshot
        ? staffingConfigFromRows({
            profiles: staffingSnapshot.profiles,
            bands: staffingSnapshot.bands,
            posts: staffingSnapshot.posts,
            workContent: staffingSnapshot.workContent,
          }).specs
        : [],
    [staffingSnapshot],
  );
  // Colour, not order, is what identifies a figure here (operator, 2026-09-02).
  // A month cell strips the labels — it is « 22 · 4 cvts », a run of numbers —
  // so the only thing left to say WHICH metric each one is, is the hue it
  // carries on the Jour and Semaine rows. `metricColor` keys on the metric key,
  // so the mapping holds across views, months, and metrics added later.
  const summaryFor = useCallback(
    (day: BoardDay | null): { key: string; text: string }[] => {
      if (!day || summaryDefs.length === 0) return [];
      const ctx = {
        day,
        profileOutputs: profileOutputsForDay({
          specs,
          day,
          workload: workloadFromDay(day),
          readyBy: checkInTime(day.arrivals),
        }),
      };
      const memo = new Map<string, number | null>();
      return summaryDefs
        .map((def) => ({
          key: def.key,
          text: formatMetric(def, evaluateMetric(registry, def.key, ctx, memo).value),
        }))
        .filter((c): c is { key: string; text: string } => c.text !== null);
    },
    [registry, summaryDefs, specs],
  );

  return (
    <div className="planning-board">
      <div className="staff-daybar">
        <button type="button" onClick={() => setMonth(shiftMonthStart(month, -1))} aria-label="Mois précédent">
          ‹
        </button>
        <FrDateInput className="pt-datepicker" value={month} onChange={(v) => v && setMonth(monthStart(v))} />
        <button type="button" onClick={() => setMonth(shiftMonthStart(month, 1))} aria-label="Mois suivant">
          ›
        </button>
        <span className="staff-daylabel">{fmtMonthLabel(month)}</span>
        {month !== thisMonth && (
          <button type="button" className="staff-today" onClick={() => setMonth(thisMonth)}>
            Ce mois-ci
          </button>
        )}
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}
      {!loaded && !err && <p className="staff-empty">Chargement…</p>}

      {loaded && (
        <>
        <table className="month-grid">
          <thead>
            <tr>
              {WEEKDAY_HEADERS.map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, i) => (
              <tr key={i}>
                {week.map((key, j) => {
                  if (!key) return <td key={j} className="month-cell is-outside" />;
                  const day = boardDays.get(key) ?? null;
                  const plannedDay = isPlannedDay(day !== null, (presencesByDay.get(key) ?? []).length, 0);
                  const gaps = plannedDay ? gapCount(key) : 0;
                  return (
                    <td key={key} className={`month-cell${key === today ? ' is-today' : ''}`}>
                      <button type="button" className="month-day" onClick={() => onOpenDay(key)}>
                        <span className="month-day-num">{Number(key.slice(8, 10))}</span>
                        {plannedDay ? (
                          <>
                            {day && summaryFor(day).length > 0 && (
                              <span className="month-day-load">
                                {summaryFor(day).map((c) => (
                                  <em key={c.key} style={{ color: metricColor(c.key) }}>
                                    {c.text}
                                  </em>
                                ))}
                              </span>
                            )}
                            {!day && <span className="month-day-load">charge non saisie</span>}
                            {gaps > 0 ? (
                              <span className="month-day-gaps">⚠ {gaps}</span>
                            ) : (
                              <span className="month-day-ok">✓</span>
                            )}
                          </>
                        ) : (
                          <span className="month-day-unplanned">—</span>
                        )}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        <PresenceMatrix
          employees={employees}
          days={days}
          presenceByDay={presenceByDay}
          today={today}
          hoursByDay={hoursByDay}
          canEditCell={canAssign ? () => true : undefined}
          onCellClick={(employeeId, date) => {
            const employee = employees.find((e) => e.id === employeeId);
            if (employee) setEditing({ employee, day: date });
          }}
        />

        {editing && (
          <EmployeeQuickEdit
            employee={editing.employee}
            color={chipColors.get(editing.employee.id)}
            anchorDay={editing.day}
            activities={activities}
            qualifications={qualifications}
            onClose={() => {
              setEditing(null);
              // The quick-edit writes straight to Supabase; this view has no
              // realtime subscription of its own, so re-read on close.
              void load(month);
            }}
          />
        )}
        </>
      )}
    </div>
  );
}
